import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, Phone, FileText, AlertTriangle, User, Info, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClienteContattiUtiliPage() {
  const { getAuthHeader } = useAuth();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const res = await axios.get(`${API}/client/my-agent`, { headers: getAuthHeader() });
        setAgent(res.data);
      } catch {
        setAgent({ has_agent: false });
      } finally {
        setLoading(false);
      }
    };
    fetchAgent();
  }, [getAuthHeader]);

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-8" data-testid="cliente-contatti-utili-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Contatti Utili
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Trova qui i riferimenti per gestire il tuo contratto e le procedure di supporto.
        </p>
      </div>

      {/* Nota informativa documenti (Fase 1.4 Punto 4 - Integrazione documenti) */}
      <Card className="border-blue-200 bg-blue-50/50" data-testid="documenti-info-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-blue-900">
            <FileText className="w-5 h-5" />
            Documenti Richiesti per l'Attivazione del Contratto
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 text-sm text-blue-900 leading-relaxed">
            <Info className="w-5 h-5 mt-0.5 flex-shrink-0 text-blue-600" />
            <p>
              Per procedere con l'attivazione del contratto, consulta l'elenco dei documenti necessari
              riportato all'interno della tua <strong>proposta</strong> e inviali via email direttamente
              all'indirizzo del tuo <strong>consulente di riferimento</strong>.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Agente di riferimento */}
      <Card data-testid="agent-info-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <User className="w-5 h-5 text-slate-600" />
            Il Tuo Consulente di Riferimento
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : agent?.has_agent ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-semibold text-lg">
                  {(agent.name || '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{agent.name}</p>
                  <p className="text-xs text-slate-500">Consulente MaCheNoleggio</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-slate-100">
                {agent.email && (
                  <a
                    href={`mailto:${agent.email}`}
                    className="flex items-center gap-2 text-sm text-slate-700 hover:text-emerald-700 transition-colors"
                    data-testid="agent-email-link"
                  >
                    <Mail className="w-4 h-4 text-slate-400" />
                    {agent.email}
                  </a>
                )}
                {agent.telefono && (
                  <a
                    href={`tel:${agent.telefono}`}
                    className="flex items-center gap-2 text-sm text-slate-700 hover:text-emerald-700 transition-colors"
                    data-testid="agent-phone-link"
                  >
                    <Phone className="w-4 h-4 text-slate-400" />
                    {agent.telefono}
                  </a>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500 py-4">
              Non risulta ancora un consulente assegnato al tuo account.
              Scrivi a <a href="mailto:supporto@machenoleggio.it" className="text-emerald-700 underline">supporto@machenoleggio.it</a>
              per essere messo in contatto.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Sinistri */}
      <div className="grid grid-cols-1 gap-4">
        <Card data-testid="sinistri-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Apertura Sinistri
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-700 space-y-2">
            <p>
              In caso di incidente o sinistro, contatta subito il numero verde della
              <strong> compagnia di noleggio</strong> indicato nel tuo contratto.
            </p>
            <p className="text-xs text-slate-500">
              Per assistenza aggiuntiva scrivi al tuo consulente di riferimento o a
              <a href="mailto:sinistri@machenoleggio.it" className="text-emerald-700 underline ml-1">sinistri@machenoleggio.it</a>.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
